import React from 'react';
import { PapiDeviation } from '@wix/da-papi-types';
import { useTranslation, Trans } from 'react-i18next';
import { normalizePremiumFolderData } from '../../../../utils/normalizePremiumFolderData';
import PremiumGallery from '@wix/da-ds/pkg/Icons/24x24/PremiumGallery';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import EyeDisc2 from '@wix/da-ds/pkg/Icons/24x24/EyeDisc2';
import Points from '@wix/da-ds/pkg/Icons/Points';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import { BiContextProvidedValue, BiData, GalleryPurchaseBiEvent } from '@wix/da-bi/pkg/events';
import { getDeviationResourceType } from '../../../_helpers';
import Button, { ProfileOwnerWatchButton } from '../../../../Button';
import { Url } from '@wix/da-url';

import s from './PremiumCover.scss';

export interface Props {
  deviation: PapiDeviation;
}

export const PremiumCover: React.FC<React.PropsWithChildren<Props>> = ({
  deviation: { premiumFolderData },
  deviation,
}) => {
  const { t } = useTranslation();
  const {
    isPremiumLocked,
    isPaidFolder,
    isWatchToAccess,
    folderId,
    folderUrl,
    formattedDollarPrice,
    pointPrice,
    subproductId,
  } = normalizePremiumFolderData(premiumFolderData);

  if (!isPremiumLocked) {
    return null;
  }

  return (
    <div className={s['paywall-wrapper']}>
      <div className={s['paywall-cover']}>
        {isPaidFolder && (
          <>
            <PremiumGallery className={s['paywall-icon']} size={IconSize.BIG} />
            <div className={s['paywall-title']}>{t('deviation.paywall.premiumFolders.title')}</div>
            <div className={s['cta-text']}>
              {/* prettier-ignore */}
              <Trans i18nKey="deviation.paywall.premiumFolders.text">
              {'To view, unlock its '}
                <BiLink className={s['folder-preview-button']} href={folderUrl}>
                  Premium Deviation
                </BiLink>
              {' — and support the artist!'}
              </Trans>
            </div>
            <div>
              <Button
                view="b18"
                height="medium"
                href={subproductId ? Url.checkoutLink(subproductId) : undefined}
                biData={BiData<GalleryPurchaseBiEvent>({
                  evid: 361,
                  typeid: getDeviationResourceType(deviation),
                  itemid: deviation.deviationId,
                  authorid: deviation.author?.useridUuid,
                  galleryid: folderId ?? 0,
                  points: pointPrice,
                  rank: BiContextProvidedValue,
                  reason_shown: BiContextProvidedValue,
                  section_typeid: BiContextProvidedValue,
                  sectionid: BiContextProvidedValue,
                  sectionname: BiContextProvidedValue,
                  sectionrank: BiContextProvidedValue,
                  sectiontype: BiContextProvidedValue,
                  sort_type: BiContextProvidedValue,
                  widgetname: BiContextProvidedValue,
                })}
              >
                {`Unlock for ${formattedDollarPrice} / `}
                <Points className={s['points-icon']} size={IconSize.TINY} disableDefaultColors />
                {`${pointPrice}`}
              </Button>
            </div>
          </>
        )}
        {isWatchToAccess && (
          <>
            <EyeDisc2 className={s['paywall-icon']} size={IconSize.BIG} />
            <div className={s['paywall-title']}>{t('deviation.paywall.watchersOnly.title')}</div>
            <div className={s['cta-text']}>{t('deviation.paywall.watchersOnly.text')}</div>
            {deviation.author && (
              <ProfileOwnerWatchButton
                className={s['watch-button']}
                user={deviation.author}
                onToggleWatchSuccess={() => {
                  window.location.href = deviation.url!;
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
PremiumCover.displayName = 'PremiumCover';

export default PremiumCover;
